/**
 * Market Research Route Definitions
 * All routes consolidated for better organization.
 */

/* eslint-disable @typescript-eslint/naming-convention */

import {
    marketAnalysisPageViewTracking,
    marketAnalysisPlayersPageViewTracking,
} from "app/pages/industry-analysis/config/pageViewTracking";
import { resolveConfigIdWorldwideWrapper } from "app/pages/industry-analysis/config/resolveConfigId";
import { IndustryAnalysisDemographicsContainer } from "app/pages/industry-analysis/demographics/IndustryAnalysisDemographicsContainer";
import type { ICategory } from "app/common/services/categoryService.types";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import { onPdfDownload } from "app/pages/industry-analysis/common/onPdfDownload";
import { WithNoCustomIndustryHOC } from "app/pages/industry-analysis/common/WithNoCustomIndustryHOC";
import {
    INDUSTRY_SEGMENTATION_CONFIG_ID,
    MARKET_PLAYERS_CONFIG_ID,
    MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID,
    MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
    MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
    MARKET_RESEARCH_HOME_CONFIG_ID,
} from "app/pages/industry-analysis/config/const";
import { redirectLegacyToComparison } from "app/pages/industry-analysis/config/legacyToComparisonRedirect";
import { onEnterIndustryResearchPageHof } from "app/pages/industry-analysis/config/onEnterIndustryResearchPageHof";
import { onRedirectUnSupportedIndustryWithSegmentsPage } from "app/pages/industry-analysis/config/onRedirectUnSupportedIndustryWithSegmentsPage";
import {
    resolveOverrideDatePickerPreset_v2,
    resolveOverrideDatePickerPreset,
} from "app/pages/industry-analysis/config/resolveOverrideDatePickerPreset";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";
import { IndustryAnalysisGeographyContainer } from "app/pages/industry-analysis/geography/IndustryAnalysisGeographyContainer";
import { IndustryAnalysisStartPageContainer } from "app/pages/industry-analysis/home/IndustryAnalysisStartPageContainer";
import MarketBuilder from "app/pages/industry-analysis/market-builder/MarketBuilder";
import { MarketPlayers } from "app/pages/industry-analysis/players/MarketPlayers";
import { IndustryAnalysisRoute } from "app/pages/industry-analysis/routes/IndustryAnalysisRoute";
import { MarketResearchOverviewRoute } from "app/pages/industry-analysis/routes/MarketResearchOverviewRoute";
import { SearchTrendsTable } from "app/pages/industry-analysis/search-trends/Table";
import { IndustrySegmentation } from "app/pages/industry-analysis/segmentation/IndustrySegmentation";
import { MarketingChannels } from "app/pages/industry-analysis/traffic-sources/MarketingChannels/MarketingChannels";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState, RouterStateConfig } from "single-spa/react-app/router/IRouterState";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { onRedirectIndustryAnalysis } from "app/routes/handlers/filter-consistency/pipes/industry-analysis/onRedirectIndustryAnalysis";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { AssetsService } from "app/services/AssetsService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { getEnabledV2Routes } from "./v2RouteDefinitions";

// Config resolver helper
const resolveConfigId = (defaultConfigID: string, categoryObject: ICategory, industryWithSegmentConfigId: string) => {
    const { country } = proNavigator.getParams() ?? {};
    if (country && country !== String(WORLDWIDE_COUNTRY_ID)) {
        return defaultConfigID;
    }
    return CategoryServiceUtils.resolveConfigID(defaultConfigID, categoryObject, industryWithSegmentConfigId);
};

// Legacy routes
const market_research_home: IRouterState = {
    parent: "digitalsuite_marketresearch",
    url: "/webmarketResearch/home",
    component: WithSegmentsLoading(() => (
        <IndustryAnalysisStartPageContainer
            pageState={"market_research_overview_v2"}
            title={"webmarket.research.homepage.mainTitle.market"}
            subtitle={"webmarketanalysis.homepage.subTitle.market"}
            marketBuilderState={"market_research_builder"}
        />
    )),
    configId: MARKET_RESEARCH_HOME_CONFIG_ID,
    configIdDeps: ["IndustryAnalysis", "IndustryAnalysisOverview", "MobileWeb", "IATopWebsitesExtended"],
    isSecondaryBarOpen: true,
    secondaryBarType: "DigitalSuiteMarketResearch",
    pageId: {
        section: "marketresearch",
        subSection: "marketresearch",
        subSubSection: "analyzewebsites",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis/Home",
        subSubSection: "Home",
    },
    pageTitle: "marketintelligence.marketresearch.webmarketanalysis.homepage.title",
    isModuleSupportSegments: true,
    entityType: "industry",
};

const market_research_builder: IRouterState = {
    parent: "digitalsuite_marketresearch",
    configId: "IndustryAnalysis",
    component: WithSegmentsLoading(MarketBuilder),
    url: "/marketresearch/webmarketanalysis/marketbuilder?category&returnToState",
    params: {
        initialDomains: {
            dynamic: true,
            inherit: false,
        },
        initialCountry: {
            dynamic: true,
            inherit: false,
        },
        internalSource: {
            dynamic: true,
            inherit: false,
        },
    },
    pageId: {
        section: "marketresearch",
        subSection: "industryAnalysis",
        subSubSection: "marketbuilder",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis/Builder",
        subSubSection: "CustomIndustryBuilder",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    secondaryBarType: "None",
    isModuleSupportSegments: true,
    customData: {
        hideSubtitle: true,
    },
};

const market_research_pages_abstract_parent: IRouterState = {
    abstract: true,
    parent: "digitalsuite_marketresearch",
    secondaryBarType3: SecondarySideBarType3.IndustryResearch,
    resolve: [resolvers.customCategories],
    url: "/markets/research",
    configId: "IndustryAnalysis",
    component: IndustryAnalysisRoute,
    data: {
        menuKbItems: false,
        disableRecording: true,
        filtersConfig: {
            duration: FiltersEnum.ENABLED,
            country: FiltersEnum.ENABLED,
            webSource: FiltersEnum.ENABLED,
        },
    },
    pageId: {
        section: "industryAnalysis",
    },
    trackingId: {
        section: "Market Research",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    navBarContainerClassName: "accordion-side-bar",
    isModuleSupportSegments: true,
    entityType: "industry",
};

const market_research_overview = {
    params: {
        comparedDuration: "",
        category: { type: "string", raw: true },
        selectedMetric: { dynamic: true },
        ...filterConsistencyParams,
    },
    parent: "market_research_pages_abstract_parent",
    url: `/researchOverview/:category/:country/:duration?webSource&selectedMetric&pageUrl&comparedDuration`,
    component: MarketResearchOverviewRoute,
    secondaryBarType3: SecondarySideBarType3.IndustryResearch,
    stateNameForEductionBar: "marketresearch_webmarketResearch_overview",
    get configId() {
        return resolveConfigIdWorldwideWrapper(
            MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
            MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
        );
    },
    configIdDeps: ["MobileWeb", "CustomSegments", "IndustryAnalysisOverview"],
    onEnter: onEnterIndustryResearchPageHof(
        MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
        MARKET_RESEARCH_PERFORMANCE_WITH_SEGMENTS_CONFIG_ID,
        resolveConfigId,
    ),
    get overrideDatepickerPreset() {
        return resolveOverrideDatePickerPreset_v2();
    },
    redirectTo: redirectPipe(redirectLegacyToComparison, onRedirectIndustryAnalysis()),
    periodOverPeriodEnabled: true,
    pageId: {
        section: "industryAnalysis",
        subSection: "overview",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "MarketResearchOverview",
    },
    icon: "sw-icon-overview",
    customData: {
        filters: {
            duration: true,
            country: true,
            category: true,
        },
    },
    pageTitle: "industryOverview.performance.page.title",
    hideSubtitle: true,
    defaultQueryParams: {
        webSource: "Total",
        duration: "13m",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    isPdfDownloadButton: true,
    pdfDownloadsMethod: onPdfDownload,
};

const market_research_traffic_channels = {
    parent: "market_research_pages_abstract_parent",
    url: "/trafficChannels/:category/:country/:duration?webSource",
    component: MarketingChannels,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_trafficChannels",
    configId: "IndustryAnalysisMarketingChannels",
    configIdDeps: ["IndustryAnalysisMarketingChannels"],
    get overrideDatepickerPreset() {
        return resolveOverrideDatePickerPreset();
    },
    redirectTo: redirectPipe(redirectLegacyToComparison, onRedirectIndustryAnalysis()),
    pageId: {
        section: "industryAnalysis",
        subSection: "traffic",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "trafficChannels",
    },
    icon: "sw-icon-traffic-sources",
    customData: {
        filters: {
            duration: true,
            country: true,
            category: true,
        },
    },
    clearSearch: true,
    pageTitle: "digitalsuite.industryAnalysis.trafficchannels.page.title",
    defaultQueryParams: {
        webSource: "Total",
        country: "999",
    },
    data: {
        filtersConfig: {
            webSource: FiltersEnum.DISABLED,
        },
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    params: {
        category: { type: "string", raw: true },
        ...filterConsistencyParams,
    },
};

const market_research_players = {
    parent: "market_research_pages_abstract_parent",
    // eslint-disable-next-line max-len
    url: "/players/:category/:country/:duration?comparedDuration?webSource?bubbleChartDisplayType&bubbleChartShowAverage&marketSharesDisplayType&marketSharesChartType&marketSharesTimeGranularity&scrollTo&useDomainsOnly&includeRegionalDomains",
    component: MarketPlayers,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_players",
    get configId() {
        return resolveConfigIdWorldwideWrapper(MARKET_PLAYERS_CONFIG_ID, MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID);
    },
    redirectTo: redirectPipe(redirectLegacyToComparison, onRedirectIndustryAnalysis()),
    onEnter: onEnterIndustryResearchPageHof(
        MARKET_PLAYERS_CONFIG_ID,
        MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID,
        resolveConfigId,
    ),
    get overrideDatepickerPreset() {
        return resolveOverrideDatePickerPreset_v2();
    },
    configIdDeps: ["MobileWeb", "IndustryAnalysis", MARKET_PLAYERS_WITH_SEGMENTS_CONFIG_ID],
    pageId: {
        section: "industryAnalysis",
        subSection: "market players",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "Market Players",
    },
    customData: {
        filters: {
            duration: false,
            country: true,
            category: true,
            unsupportedCountryTooltip: "topsites.unsupportedCountry.tooltip",
        },
    },
    clearSearch: true,
    pageTitle: "industry.research.pages.market.players.title",
    pageSubtitle: "industry.research.pages.market.players.title.tooltip",
    defaultQueryParams: {
        webSource: "Total",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    params: {
        category: { type: "string", raw: true },
        marketSharesDisplayType: { type: "string", dynamic: true },
        marketSharesChartType: { type: "string", dynamic: true },
        marketSharesTimeGranularity: { type: "string", dynamic: true },
        scrollTo: { type: "string" },
        bubbleChartDisplayType: { type: "string", dynamic: true },
        bubbleChartShowAverage: { type: "string", dynamic: true },
        includeRegionalDomains: { type: "string", dynamic: true },
        ...filterConsistencyParams,
    },
    isSecondaryBarOpen: true,
    periodOverPeriodEnabled: true,
    data: {
        pageViewTracking: marketAnalysisPlayersPageViewTracking,
    },
};

const market_research_segmentation = {
    params: {
        comparedDuration: "",
        category: { type: "string", raw: true },
        overTimeChartDisplayType: { type: "string", dynamic: true },
        growthOverviewMetric: { type: "string", dynamic: true },
        growthOverviewGranularity: { type: "string", dynamic: true },
        ...filterConsistencyParams,
    },
    parent: "market_research_pages_abstract_parent",
    url: `/segmentation/:country/:duration?webSource&category&comparedDuration&overTimeChartDisplayType&growthOverviewMetric&growthOverviewGranularity`,
    component: WithNoCustomIndustryHOC(IndustrySegmentation),
    stateNameForEductionBar: "marketresearch_webmarketanalysis_segmentation",
    configId: INDUSTRY_SEGMENTATION_CONFIG_ID,
    configIdDeps: ["MobileWeb", "CustomSegments"],
    redirectTo: redirectPipe(
        redirectLegacyToComparison,
        onRedirectUnSupportedIndustryWithSegmentsPage(),
        onRedirectIndustryAnalysis(),
    ),
    periodOverPeriodEnabled: true,
    pageId: {
        section: "industryAnalysis",
        subSection: "segmentation",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "marketSegments",
    },
    customData: {
        filters: {
            duration: true,
            country: true,
            category: true,
        },
    },
    pageTitle: "industryAnalysis.segmentation.page.title",
    pageSubtitle: "industryAnalysis.segmentation.page.tooltip",
    defaultQueryParams: {
        webSource: "Total",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    data: {
        pageViewTracking: marketAnalysisPageViewTracking,
    },
};

const market_research_geography = {
    parent: "market_research_pages_abstract_parent",
    url: "/geography/:category/:country/:duration?webSource",
    stateNameForEductionBar: "marketresearch_webmarketanalysis_geography",
    component: IndustryAnalysisGeographyContainer,
    configId: "MarketResearchGeographics",
    redirectTo: redirectPipe(
        redirectLegacyToComparison,
        onRedirectUnSupportedIndustryWithSegmentsPage(),
        onRedirectIndustryAnalysis(),
    ),
    pageId: {
        section: "industryAnalysis",
        subSection: "geography",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "geography",
    },
    customData: {
        filters: {
            duration: true,
            country: false,
            category: true,
        },
    },
    clearSearch: true,
    pageTitle: "digitalsuite.industryAnalysis.geography.page.title",
    pageSubtitle: "industryAnalysis.geography.page.subtitle",
    data: {
        filtersConfig: {
            country: FiltersEnum.HIDDEN,
        },
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    params: {
        category: { type: "string", raw: true },
        ...filterConsistencyParams,
    },
    skipCountryCheck: true,
};

const market_research_demand_trends = {
    parent: "market_research_pages_abstract_parent",
    // eslint-disable-next-line max-len
    url: "/searchtrends/:category/:country/:duration?tab&channel&includeBranded&includeNoneBranded&excludeBranded&orderBy&search&BooleanSearchTerms?webSource?IncludeTrendingKeywords?IncludeNewKeywords?IncludeQuestions",
    component: SearchTrendsTable,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_searchtrends",
    configId: "IndustrySearchInterests",
    configIdDeps: ["KeywordAnalysis"],
    params: {
        includeNoneBranded: "true",
        includeBranded: "false",
        category: { type: "string", raw: true },
        tab: { dynamic: true },
        ...filterConsistencyParams,
    },
    pageId: {
        section: "industryAnalysis",
        subSection: "audience",
        subSubSection: "searchTrends",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "searchTrends",
    },
    icon: "sw-icon-top-keywords",
    customData: {
        filters: {
            duration: true,
            country: true,
            category: true,
        },
        notPermittedConfig: {
            image: AssetsService.assetUrl("images/IATopKeywords.png"),
            description: "ia.topkeywords.desc",
        },
    },
    clearSearch: true,
    pageTitle: "industryAnalysis.demand.trends.title",
    pageSubtitle: "industryAnalysis.searchTrends.subtitle",
    defaultQueryParams: {
        webSource: "Desktop",
    },
    homeState: "digitalsuite_webmarketanalysis_home",
    redirectTo: redirectPipe(
        redirectLegacyToComparison,
        onRedirectUnSupportedIndustryWithSegmentsPage(),
        onRedirectIndustryAnalysis(),
    ),
    data: {
        pageViewTracking: marketAnalysisPageViewTracking,
    },
};

const market_research_demography = {
    parent: "market_research_pages_abstract_parent",
    configId: "MarketResearchDemographics",
    configIdDeps: ["MobileWeb"],
    url: "/demographics/:category/:country/:duration?webSource",
    component: IndustryAnalysisDemographicsContainer,
    stateNameForEductionBar: "marketresearch_webmarketanalysis_demographics",
    redirectTo: redirectPipe(
        redirectLegacyToComparison,
        onRedirectUnSupportedIndustryWithSegmentsPage(),
        onRedirectIndustryAnalysis(),
    ),
    pageId: {
        section: "industryAnalysis",
        subSection: "overview",
        subSubSection: "demographics",
    },
    trackingId: {
        section: "Market Research",
        subSection: "Market Analysis",
        subSubSection: "demographics",
    },
    customData: {
        filters: {
            duration: true,
            country: true,
            category: true,
            webSource: true,
        },
    },
    defaultQueryParams: {
        webSource: "Total",
        country: "999",
    },
    pageTitle: "industryAnalysis.demographics.page.title",
    homeState: "digitalsuite_webmarketanalysis_home",
    params: {
        category: { type: "string", raw: true },
        ...filterConsistencyParams,
    },
};

// Export V2 routes

// All routes consolidated
export const marketResearchRoutes: RouterStateConfig = {
    market_research_home,
    market_research_builder,
    market_research_pages_abstract_parent,
    market_research_overview,
    market_research_traffic_channels,
    market_research_players,
    market_research_segmentation,
    market_research_geography,
    market_research_demand_trends,
    market_research_demography,
    ...getEnabledV2Routes(),
};
